// La tarjeta principal del panel: cuanto se vendio este mes, a que ritmo y
// como viene la curva.
//
// Es la unica tarjeta oscura de la pagina. Eso no es decoracion: marca que
// aqui esta la cifra que manda, y todo lo demas es contexto de esta.

import { useMemo } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { LineChart } from '@mui/x-charts/LineChart';

import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import TrendingDownRoundedIcon from '@mui/icons-material/TrendingDownRounded';

import { soles, numero, nombreMes, mesAnterior, diaYMes } from './formato';
import { useConteo, SUAVE } from './motion';

const TINTA = '#183A2A';
const MENTA = '#3CFB9F';
const CREMA = '#FCFCFA';
const RENUEVA = '#7FD8E3';

function Leyenda({ color, texto }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: color }} />
      <Typography sx={{ fontSize: 12.5, color: 'rgba(252,252,250,.65)' }}>{texto}</Typography>
    </Stack>
  );
}

function Dato({ etiqueta, valor, detalle }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 12, color: 'rgba(252,252,250,.55)', mb: .25 }}>{etiqueta}</Typography>
      <Typography sx={{ fontSize: 19, fontWeight: 700, color: CREMA, lineHeight: 1.2 }}>{valor}</Typography>
      {detalle &&
        <Typography sx={{ fontSize: 12, color: 'rgba(252,252,250,.5)' }}>{detalle}</Typography>
      }
    </Box>
  );
}

export default function VentasHero({ datos }) {
  const { mes, serieVentas, ritmoDiario, ritmoDiarioAnterior, variacionRitmo, proyeccionCierre, hoy } = datos;

  /* El dia de la carga inicial vendio veinte veces mas que el mejor dia real.
     Si el eje se estira hasta ahi, los otros sesenta dias quedan pegados al
     piso y no se distingue una buena semana de una mala. El eje se corta en
     la altura de los dias normales y ese dia se sale del grafico, con su
     aviso debajo. El dato no se toca: el globo sigue mostrando el monto. */
  const { curva, topeEje, diaDeCarga } = useMemo(() => {
    /* La curva arranca el primer dia con ventas registradas. Dibujar los
       dias anteriores con cero no dice "no se vendio", dice "no existia el
       sistema", y deja medio grafico en blanco como si estuviera roto. */
    const primero = serieVentas.findIndex((p) => Number(p.monto) > 0);
    const visible = serieVentas.slice(primero < 0 ? 0 : primero);

    const normales = visible.filter((p) => !p.carga).map((p) => Number(p.monto));
    const tope = Math.ceil((Math.max(...normales, 1) * 1.15) / 500) * 500;

    return { curva: visible, topeEje: tope, diaDeCarga: visible.find((p) => p.carga) };
  }, [serieVentas]);

  /* El dia de carga se dibuja tocando el techo en vez de salirse de la caja:
     el grafico no recorta y la linea quedaba flotando sobre el titulo. El
     monto de verdad va en la nota de abajo. */
  const alturaVisible = (valor) => Math.min(Number(valor), topeEje);
  const etiquetas = curva.map((p) => p.fecha);
  const ventaEnCurso = useConteo(mes.monto, 1200);
  const sube = Number(variacionRitmo) >= 0;

  return (
    <Card
      sx={{
        p: { xs: 2.5, md: 3.5 },
        position: 'relative',
        overflow: 'hidden',
        bgcolor: TINTA,
        border: 'none',
        backgroundImage: `radial-gradient(900px 320px at 88% -40%, rgba(60,251,159,.22), transparent 70%)`,
        boxShadow: (t) => t.customShadows.z16,
        /* El resplandor de menta respira muy despacio. Es lo unico que se
           mueve solo en la pagina y da la sensacion de panel encendido. */
        '@keyframes respira': {
          '0%, 100%': { opacity: .75, transform: 'scale(1)' },
          '50%': { opacity: 1, transform: 'scale(1.08)' },
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: -140, right: -90, width: 420, height: 320,
          borderRadius: '50%',
          pointerEvents: 'none',
          background: 'radial-gradient(circle, rgba(60,251,159,.20), transparent 68%)',
          animation: `respira 9s ${SUAVE} infinite`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: 'minmax(260px, 340px) 1fr' },
          gap: { xs: 3, lg: 4 },
          alignItems: 'stretch',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography sx={{ fontSize: 13, color: 'rgba(252,252,250,.6)' }}>
            Ventas de {nombreMes(hoy)}
          </Typography>

          <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mt: .5, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: { xs: 40, md: 52 }, fontWeight: 800, letterSpacing: '-2px', color: CREMA, lineHeight: 1 }}>
              {soles(ventaEnCurso)}
            </Typography>

            {variacionRitmo !== null && variacionRitmo !== undefined &&
              <Stack
                direction="row" alignItems="center" spacing={.4}
                sx={{
                  px: 1.1, py: .4, borderRadius: 999, flexShrink: 0,
                  bgcolor: sube ? 'rgba(60,251,159,.16)' : 'rgba(237,162,148,.18)',
                  color: sube ? MENTA : '#EDA294',
                  fontSize: 13, fontWeight: 700,
                }}
              >
                {sube ? <TrendingUpRoundedIcon sx={{ fontSize: 17 }} /> : <TrendingDownRoundedIcon sx={{ fontSize: 17 }} />}
                {sube ? '+' : ''}{Math.round(variacionRitmo)}%
              </Stack>
            }
          </Stack>

          <Typography sx={{ fontSize: 13, color: 'rgba(252,252,250,.6)', mt: 1 }}>
            {numero(mes.operaciones)} ventas · {numero(mes.nuevos)} nuevos y {numero(mes.renovaciones)} renovaciones
          </Typography>

          <Box
            sx={{
              mt: 2.5, pt: 2.5, display: 'grid', gap: 2.5,
              gridTemplateColumns: { xs: '1fr 1fr', lg: '1fr 1fr' },
              borderTop: '1px solid rgba(252,252,250,.12)',
            }}
          >
            <Dato
              etiqueta="Ritmo por día"
              valor={soles(ritmoDiario)}
              detalle={`en ${mesAnterior(hoy)} fue ${soles(ritmoDiarioAnterior)}`}
            />
            <Dato
              etiqueta="Cierre estimado"
              valor={soles(proyeccionCierre)}
              detalle="si se mantiene el ritmo"
            />
            <Dato
              etiqueta="De clientes nuevos"
              valor={soles(mes.montoNuevos)}
              detalle={`${Math.round((mes.montoNuevos / (mes.monto || 1)) * 100)}% de la venta`}
            />
            <Dato
              etiqueta="De renovaciones"
              valor={soles(mes.montoRenovaciones)}
              detalle={`${Math.round((mes.montoRenovaciones / (mes.monto || 1)) * 100)}% de la venta`}
            />
          </Box>
        </Box>

        <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" spacing={1}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: 'rgba(252,252,250,.8)' }}>
              Venta de cada día
            </Typography>
            <Stack direction="row" spacing={2}>
              <Leyenda color={MENTA} texto="Clientes nuevos" />
              <Leyenda color={RENUEVA} texto="Renovaciones" />
            </Stack>
          </Stack>

          <Box sx={{ flex: 1, minHeight: 236, mt: .5 }}>
            <LineChart
              height={236}
              margin={{ top: 12, right: 8, bottom: 22, left: 8 }}
              series={[
                { id: 'nuevos', data: curva.map((p) => alturaVisible(p.montoNuevos)), label: 'Nuevos',
                  color: MENTA, area: true, stack: 'venta', showMark: false, curve: 'monotoneX' },
                { id: 'renueva', data: curva.map((p) => alturaVisible(p.montoRenovaciones)), label: 'Renovaciones',
                  color: RENUEVA, area: true, stack: 'venta', showMark: false, curve: 'monotoneX' },
              ]}
              xAxis={[{
                scaleType: 'point',
                data: etiquetas,
                valueFormatter: (v) => diaYMes(v),
                // Una etiqueta por semana: sesenta fechas seguidas no se leen.
                tickInterval: (v, i) => i % 7 === 0,
              }]}
              /* Sin formateador propio en el eje: el de la libreria ya pone
                 los miles con coma, y cualquier formateador nuestro aqui
                 tumbaba el grafico entero al dibujar las marcas. */
              yAxis={[{ max: topeEje }]}
              /* La leyenda propia del grafico se apaga: la de arriba a la
                 derecha es la que acompana al titulo. */
              hideLegend
              sx={{
                '& .MuiChartsAxis-line, & .MuiChartsAxis-tick': { stroke: 'rgba(252,252,250,.18)' },
                '& .MuiChartsAxis-tickLabel': { fill: 'rgba(252,252,250,.55) !important', fontSize: 11 },
                '& .MuiLineChart-area': { fillOpacity: .28 },
              }}
            />
          </Box>

          {diaDeCarga &&
            <Typography sx={{ fontSize: 11.5, color: 'rgba(252,252,250,.45)', mt: .5 }}>
              El {diaYMes(diaDeCarga.fecha)} se sale del gráfico: {soles(diaDeCarga.monto)} de la carga
              inicial de clientes que ya eran clientes antes del sistema. No cuenta en el ritmo por día.
            </Typography>
          }
        </Box>
      </Box>
    </Card>
  );
}
